import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Tooltip, Alert, CircularProgress, Divider
} from '@mui/material';
import {
  ArrowBack, Download, Receipt, CheckCircle, Warning, PendingActions, PictureAsPdf
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function EstadoCuenta() {
  const navigate = useNavigate();
  const { clienteId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => { cargar(); }, [clienteId]);

  const cargar = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/cliente/${clienteId}/estado-cuenta`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar estado de cuenta');
    } finally { setLoading(false); }
  };

  const fmt = (v) => `$${parseFloat(v || 0).toFixed(2)}`;

  const descargarEstadoCuenta = async () => {
    if (!data) return;
    try {
      const response = await fetch(`${API_URL}/api/cliente/${clienteId}/estado-cuenta/pdf`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error("Error generando PDF");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {
      console.error("Error descargando PDF:", err);
      // Fallback: descargar JSON
      const contenido = { cliente: data.cliente, resumen: data.resumen, facturas: data.facturas, fechaGeneracion: new Date().toISOString() };
      const blob = new Blob([JSON.stringify(contenido, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `estado_cuenta_${data.cliente?.identificacion || clienteId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert><Button startIcon={<ArrowBack />} onClick={() => navigate('/clientes')} sx={{ mt: 2 }}>Volver</Button></Box>;
  if (!data) return null;

  const { cliente, resumen, facturas } = data;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/clientes')}><ArrowBack /></IconButton>
          <Box>
            <Typography variant="h5" fontWeight={700}>Estado de Cuenta</Typography>
            <Typography variant="body2" color="text.secondary">
              {cliente?.razon_social || cliente?.nombre} — {cliente?.identificacion}
            </Typography>
          </Box>
        </Box>
        <Button variant="outlined" startIcon={<Download />} onClick={descargarEstadoCuenta}>
          Descargar
        </Button>
      </Box>

      {/* Resumen */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: "Total Facturado", value: fmt(resumen.totalFacturado), color: "#1976d2" },
          { label: "Total Cobrado", value: fmt(resumen.totalCobrado), color: "#388e3c" },
          { label: "Saldo Pendiente", value: fmt(resumen.saldoPendiente), color: "#d32f2f" },
          { label: "Crédito Disponible", value: fmt(resumen.creditoDisponible), color: "#7b1fa2" },
        ].map((s, i) => (
          <Grid item xs={6} md={3} key={i}>
            <Card>
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: s.color }}>{s.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {resumen.limiteCredito > 0 && (
        <Alert severity={resumen.creditoDisponible > 0 ? "info" : "warning"} sx={{ mb: 3 }}>
          Límite de crédito: {fmt(resumen.limiteCredito)} | Plazo: {cliente?.plazo_credito_dias || 0} días |
          Disponible: {fmt(resumen.creditoDisponible)}
        </Alert>
      )}

      {/* Tabla de Facturas */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Facturas ({facturas.length})</Typography>
        <Divider sx={{ mb: 2 }} />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                <TableCell><strong>Fecha</strong></TableCell>
                <TableCell><strong>No. Factura</strong></TableCell>
                <TableCell align="right"><strong>Total</strong></TableCell>
                <TableCell align="right"><strong>Cobrado</strong></TableCell>
                <TableCell align="right"><strong>Saldo</strong></TableCell>
                <TableCell align="center"><strong>Estado SRI</strong></TableCell>
                <TableCell align="center"><strong>Pago</strong></TableCell>
                <TableCell align="center"><strong>PDF</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {facturas.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No hay facturas para este cliente</Typography>
                </TableCell></TableRow>
              ) : facturas.map((f) => (
                <TableRow key={f.claveAcceso} hover>
                  <TableCell>{f.fechaEmision}</TableCell>
                  <TableCell>{String(f.secuencial || '').padStart(9, '0')}</TableCell>
                  <TableCell align="right"><strong>{fmt(f.total)}</strong></TableCell>
                  <TableCell align="right" sx={{ color: 'success.main' }}>{fmt(f.totalCobrado)}</TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={600} color={f.saldoPendiente > 0 ? "error.main" : "success.main"}>
                      {fmt(f.saldoPendiente)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={f.estadoSRI} size="small"
                      color={f.estadoSRI === "AUTORIZADO" ? "success" : f.estadoSRI === "PENDIENTE" ? "warning" : "error"} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small"
                      label={f.estadoPago}
                      icon={f.estadoPago === "PAGADA" ? <CheckCircle /> : f.estadoPago === "PARCIAL" ? <Warning /> : <PendingActions />}
                      color={f.estadoPago === "PAGADA" ? "success" : f.estadoPago === "PARCIAL" ? "warning" : "error"} />
                  </TableCell>
                  <TableCell align="center">
                    {f.pdfPath && (
                      <Tooltip title="Ver PDF">
                        <IconButton size="small" color="primary"
                          onClick={() => window.open(`${API_URL}/${f.pdfPath}`, '_blank')}>
                          <PictureAsPdf />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
